import { Link } from 'react-router-dom';
import type { ExtractedEvent } from '../types';
import { useEventImageUrl } from '../lib/image-sas';
import {
  compareEventDates, dateTileParts, eventDayKey, formatDateHeader,
  formatRange, isAllDayEvent, shortDateLabel, timePillParts,
} from '../lib/eventDates';
import { StaleRecurringChip } from './StaleRecurringChip';
import { visibleChipTags, formatTag } from '../lib/tagTaxonomy';
import EventTagChips from './EventTagChips';
import EventReactionButtons from './EventReactionButtons';
import EntityFollowChip from './EntityFollowChip';
import MatchBadge from './MatchBadge';
import EventDateMenu from './EventDateMenu';
import EventReviewWarningChips from './EventReviewWarningChips';
import {
  DateProofSchedule, DateProofWarning, dateProofRangeOptions, nonTemporalWarnings, useDateProofEvents,
} from '../lib/dateProofReview';
import type { DateCorrection } from '../lib/useRecalculateDate';
import { useFavorites } from '../lib/favorites';
import { useTasteMatch, type TasteMatch } from '../lib/tasteMatch';

export type EventListDensity = 'compact' | 'cards';

export interface EventListProps {
  items: ExtractedEvent[];
  density: EventListDensity;
  /** When true, items are bucketed under date-group headers (matches HomePage). */
  groupByDate?: boolean;
  /** Header sort direction; only meaningful when `groupByDate` is true. Default 'asc'. */
  groupSort?: 'asc' | 'desc';
  /** Hides venue/neighborhood metadata when the list is already scoped to a venue. */
  hideVenue?: boolean;
  /** Hides series chips when the list is already scoped to a series. */
  hideSeries?: boolean;
  /** Card grid column class for `<li>`. Defaults to events-page sizing. */
  cardColClassName?: string;
  isAdmin?: boolean;
  onSelect: (e: ExtractedEvent) => void;
  onEdit?: (e: ExtractedEvent) => void;
  /** Admin-only: hide/reject the event from the public list. When provided,
   *  a small "hide" icon button appears alongside the edit pencil so admins
   *  can dismiss spam-looking entries inline. The handler typically opens
   *  the shared `useEventRejection` confirm modal. */
  onHide?: (e: ExtractedEvent) => void;
  /** Admin-only: re-runs the AI extractor against the original raw post.
   *  When provided, a small refresh icon appears next to the edit pencil so
   *  admins can correct mis-extracted dates / details without leaving the
   *  list. The handler is responsible for showing any in-flight indicator. */
  onRecalculateDate?: (e: ExtractedEvent) => void;
  /** Set of event rowKeys that are currently being recalculated. Used to
   *  disable the icon button + show a spinner while the work is in flight. */
  recalculatingIds?: ReadonlySet<string>;
  /** Admin-only: flag the event's date/time as wrong (optionally with a
   *  corrected date). Paired with `onRecalculateDate` inside the calendar
   *  menu. Without a correction the event is badged "Wrong date"; with one the
   *  date is fixed in place. */
  onMarkWrongDate?: (e: ExtractedEvent, correction?: DateCorrection) => void;
  /** Set of event rowKeys currently being marked wrong / corrected. */
  markingWrongIds?: ReadonlySet<string>;
  /** When provided (admin context), enables inline tag quick-edit on each
   *  rendered chip strip. Receives the merged event after each tag mutation
   *  so the parent list can update in place. */
  onEventUpdated?: (e: ExtractedEvent) => void;
  ariaLabel?: string;
  /** Overrides the global taste-match scorer for each row. When omitted, the
   *  app-wide `useTasteMatch()` profile is used so a match badge appears
   *  wherever the user has enough marked events. Pass an explicit scorer to
   *  blend in page-specific signals (e.g. embedding scores on the taste page). */
  match?: (e: ExtractedEvent) => TasteMatch | null;
}

const DEFAULT_CARD_COL = 'col-sm-6 col-lg-4 col-xl-3';

/**
 * Shared event renderer used by HomePage, VenueDetailPage, and SeriesDetailPage.
 * Centralises compact vs card density, date-group headers, admin edit affordances,
 * and metadata chips so the experience stays in lockstep across the app.
 */
export default function EventList(props: EventListProps) {
  const { density, groupByDate, groupSort = 'asc' } = props;
  const items = useDateProofEvents(props.items);

  if (!groupByDate) {
    return density === 'compact'
      ? <CompactList {...rendererProps(props, items)} showDate />
      : <CardGrid {...rendererProps(props, items)} />;
  }

  const map = new Map<string, ExtractedEvent[]>();
  for (const e of items) {
    const k = eventDayKey(e) ?? '__invalid__';
    const arr = map.get(k);
    if (arr) arr.push(e);
    else map.set(k, [e]);
  }
  // Within each day group: all-day events float to the top, then timed
  // events ascend by startUtc. The parent list's outer sort can put any
  // ordering on the events array (date-asc, distance, etc.), but inside a
  // single day we always want the natural reading order so an "All day"
  // banner doesn't end up wedged between noon and afternoon entries.
  for (const arr of map.values()) {
    arr.sort(compareEventDates);
  }
  const entries = [...map.entries()].sort(([a], [b]) => {
    if (a === '__invalid__') return b === a ? 0 : 1;
    if (b === '__invalid__') return -1;
    return groupSort === 'desc' ? b.localeCompare(a) : a.localeCompare(b);
  });

  return (
    <>
      {entries.map(([dayKey, group]) => (
        <section key={dayKey} className="mh-event-group" aria-labelledby={`grp-${dayKey}`}>
          <h2 id={`grp-${dayKey}`} className="mh-event-group__header">
            <span className="mh-event-group__title">{formatDateHeader(dayKey)}</span>
            <span className="mh-event-group__count">
              {group.length} event{group.length === 1 ? '' : 's'}
            </span>
          </h2>
          {density === 'compact'
            ? <CompactList {...rendererProps(props, group)} showDate={false} />
            : <CardGrid {...rendererProps(props, group)} />}
        </section>
      ))}
    </>
  );
}

export interface EventDensityToggleProps {
  density: EventListDensity;
  onChange: (d: EventListDensity) => void;
  className?: string;
}

/** Compact/Cards toggle button group — visually identical across pages. */
export function EventDensityToggle({ density, onChange, className }: EventDensityToggleProps) {
  const isCompact = density === 'compact';
  const isCards = density === 'cards';
  return (
    <div className={`btn-group ${className ?? ''}`.trim()} role="group" aria-label="List density">
      <button
        type="button"
        className={`btn btn-sm ${isCompact ? 'btn-secondary' : 'btn-outline-secondary'}`}
        onClick={() => onChange('compact')}
        aria-pressed={isCompact}
        title="Compact list"
      >
        <i className="bi bi-list-task me-1" aria-hidden="true" />
        Compact
      </button>
      <button
        type="button"
        className={`btn btn-sm ${isCards ? 'btn-secondary' : 'btn-outline-secondary'}`}
        onClick={() => onChange('cards')}
        aria-pressed={isCards}
        title="Card grid"
      >
        <i className="bi bi-grid me-1" aria-hidden="true" />
        Cards
      </button>
    </div>
  );
}

// ---------- internal renderers ----------

interface RendererProps {
  items: ExtractedEvent[];
  isAdmin?: boolean;
  onSelect: (e: ExtractedEvent) => void;
  onEdit?: (e: ExtractedEvent) => void;
  onHide?: (e: ExtractedEvent) => void;
  onRecalculateDate?: (e: ExtractedEvent) => void;
  recalculatingIds?: ReadonlySet<string>;
  onMarkWrongDate?: (e: ExtractedEvent, correction?: DateCorrection) => void;
  markingWrongIds?: ReadonlySet<string>;
  onEventUpdated?: (e: ExtractedEvent) => void;
  hideVenue?: boolean;
  hideSeries?: boolean;
  cardColClassName?: string;
  ariaLabel?: string;
  /** When true, each compact row shows a short date label above its start time.
   *  Driven by `!groupByDate` so flat (ungrouped) lists still tell the user
   *  which day an event falls on; grouped lists carry the date in the header
   *  instead. Ignored by the card renderer, which always shows the full date. */
  showDate?: boolean;
  match?: (e: ExtractedEvent) => TasteMatch | null;
}

function rendererProps(props: EventListProps, items: ExtractedEvent[]): RendererProps {
  return {
    items,
    isAdmin: props.isAdmin,
    onSelect: props.onSelect,
    onEdit: props.onEdit,
    onHide: props.onHide,
    onRecalculateDate: props.onRecalculateDate,
    recalculatingIds: props.recalculatingIds,
    onMarkWrongDate: props.onMarkWrongDate,
    markingWrongIds: props.markingWrongIds,
    onEventUpdated: props.onEventUpdated,
    hideVenue: props.hideVenue,
    hideSeries: props.hideSeries,
    cardColClassName: props.cardColClassName,
    ariaLabel: props.ariaLabel,
    match: props.match,
  };
}

function CompactList({ items, isAdmin, onSelect, onEdit, onHide, onRecalculateDate, recalculatingIds, onMarkWrongDate, markingWrongIds, onEventUpdated, hideVenue, hideSeries, ariaLabel, showDate, match }: RendererProps) {
  const resolveImage = useEventImageUrl();
  const fav = useFavorites();
  const tasteMatch = useTasteMatch();
  const resolveMatch = match ?? tasteMatch.match;
  return (
    <ul className="list-unstyled mh-event-list" aria-label={ariaLabel ?? 'Event results'}>
      {items.map((e) => {
        const imageSrc = resolveImage(e.imageUrl);
        const allDay = isAllDayEvent(e);
        const tags = visibleChipTags(e.tags, e);
        const time = timePillParts(e);
        const tile = dateTileParts(e);
        const savedClass = fav.isSaved(e.rowKey) ? ' mh-event-row--saved' : '';
        return (
          <li
            key={e.rowKey}
            className={`mh-event-row${savedClass}`}
          >
            <DateProofSchedule event={e}>
            <div
              className={`mh-event-row__time flex-shrink-0${allDay ? ' mh-event-row__time--allday' : ''}`}
            >
              {showDate && (
                <span className="mh-event-row__time-date">{shortDateLabel(e)}</span>
              )}
              <span className="mh-event-row__time-start">{time.start}</span>
              {time.end && <span className="mh-event-row__time-end">{time.end}</span>}
            </div>
            </DateProofSchedule>
            <div className="mh-event-row__body">
              <div className="mh-event-row__title-line">
                <button
                  type="button"
                  className="btn btn-link p-0 text-start mh-event-title mh-event-row__title"
                  onClick={() => onSelect(e)}
                >
                  {e.title}
                </button>
                <MatchBadge match={resolveMatch(e)} className="mh-event-row__chip" />
                <EventReviewWarningChips warnings={nonTemporalWarnings(e.reviewWarnings)} />
                {e.dateFlaggedWrong && (
                  <span
                    className="badge mh-wrong-date-chip mh-event-row__chip"
                    title="An admin flagged this date/time as wrong"
                  >
                    <i className="bi bi-calendar-x" aria-hidden="true" /> Wrong date
                  </span>
                )}
                {isAdmin && onEdit && (
                  <button
                    type="button"
                    className="btn btn-link p-0 text-muted mh-event-row__edit"
                    onClick={(ev) => {
                      ev.stopPropagation();
                      onEdit(e);
                    }}
                    aria-label={`Edit ${e.title}`}
                    title="Edit event"
                  >
                    <i className="bi bi-pencil-square" aria-hidden="true" />
                  </button>
                )}
                {isAdmin && onHide && (
                  <button
                    type="button"
                    className="btn btn-link p-0 text-muted mh-event-row__edit"
                    onClick={(ev) => {
                      ev.stopPropagation();
                      onHide(e);
                    }}
                    aria-label={`Hide ${e.title}`}
                    title="Hide event"
                  >
                    <i className="bi bi-eye-slash" aria-hidden="true" />
                  </button>
                )}
                {isAdmin && onRecalculateDate && onMarkWrongDate && (
                  <EventDateMenu
                    event={e}
                    variant="compact"
                    recalcBusy={recalculatingIds?.has(e.rowKey) ?? false}
                    markBusy={markingWrongIds?.has(e.rowKey) ?? false}
                    onRecalculate={onRecalculateDate}
                    onMarkWrong={onMarkWrongDate}
                  />
                )}
                {e.eventType && (
                  <span className="badge text-bg-primary mh-event-row__chip">{formatTag(e.eventType)}</span>
                )}
                {e.priceInfo && (
                  <span className="badge text-bg-success mh-event-row__chip">{e.priceInfo}</span>
                )}
                {e.ageRestriction && (
                  <span className="badge text-bg-warning text-dark mh-event-row__chip">
                    {formatTag(e.ageRestriction)}
                  </span>
                )}
                {e.recurringPattern && e.recurringPattern !== 'one-time' && (
                  <span
                    className="badge text-bg-info text-dark mh-event-row__chip"
                    title={
                      e.recurringDaysOfWeek?.length
                        ? `Recurs ${formatTag(e.recurringPattern)} on ${e.recurringDaysOfWeek.join(', ')}`
                        : `Recurs ${formatTag(e.recurringPattern)}`
                    }
                  >
                    🔁 {formatTag(e.recurringPattern)}
                  </span>
                )}
                <StaleRecurringChip event={e} className="mh-event-row__chip" />
              </div>
              <div className="mh-event-row__meta">
                <time dateTime={e.startUtc} className="visually-hidden">
                  {formatRange(e, dateProofRangeOptions(e))}
                </time>
                {!hideVenue && (e.venueName || e.locationText) && (
                  <span className="mh-event-row__venue">
                    <i className="bi bi-geo-alt me-1" aria-hidden="true" />
                    {e.venueId ? (
                      <Link to={`/venues/${e.venueId}`}>{e.venueName ?? e.locationText}</Link>
                    ) : (
                      e.venueName ?? e.locationText
                    )}
                    {e.neighborhood && <span className="text-muted"> · {e.neighborhood}</span>}
                    {e.venueId && (
                      <EntityFollowChip
                        kind="venue"
                        targetId={e.venueId}
                        targetName={e.venueName ?? e.locationText ?? 'this venue'}
                        className="ms-2"
                      />
                    )}
                  </span>
                )}
                {!hideSeries && e.seriesId && e.seriesName && (
                  <span className="badge rounded-pill text-bg-info text-dark mh-event-row__chip">
                    <i className="bi bi-collection me-1" aria-hidden="true" />
                    <Link to={`/series/${e.seriesId}`} className="text-reset text-decoration-none">
                      {e.seriesName}
                    </Link>
                  </span>
                )}
                {!hideSeries && e.seriesId && e.seriesName && (
                  <EntityFollowChip
                    kind="series"
                    targetId={e.seriesId}
                    targetName={e.seriesName}
                    className="ms-1"
                  />
                )}
                {tags.length > 0 && (
                  <EventTagChips
                    event={e}
                    max={3}
                    chipClassName="badge rounded-pill text-bg-light border mh-event-row__chip"
                    className="d-inline-flex flex-wrap align-items-center gap-1 position-relative"
                    onChange={onEventUpdated}
                  />
                )}
                {tags.length === 0 && onEventUpdated && (
                  <EventTagChips
                    event={e}
                    chipClassName="badge rounded-pill text-bg-light border mh-event-row__chip"
                    className="d-inline-flex align-items-center position-relative"
                    onChange={onEventUpdated}
                  />
                )}
              </div>
              <div className="mh-event-row__actions">
                <EventReactionButtons event={e} />
              </div>
            </div>
            <button
              type="button"
              className="mh-event-row__thumb-btn"
              onClick={() => onSelect(e)}
              aria-label={`View details for ${e.title}`}
            >
              {imageSrc ? (
                <img src={imageSrc} alt="" loading="lazy" className="mh-event-row__thumb" />
              ) : (
                <div className="mh-event-row__thumb mh-event-row__thumb--date" aria-hidden="true">
                  <span className={`mh-event-row__thumb-day${tile.weekday ? '' : ' mh-event-row__thumb-day--month'}`}>
                    {tile.day}
                  </span>
                  <span className="mh-event-row__thumb-month">
                    {tile.month}
                  </span>
                </div>
              )}
            </button>
          </li>
        );
      })}
    </ul>
  );
}

function CardGrid({
  items,
  isAdmin,
  onSelect,
  onEdit,
  onHide,
  onRecalculateDate,
  recalculatingIds,
  onMarkWrongDate,
  markingWrongIds,
  onEventUpdated,
  hideVenue,
  hideSeries,
  cardColClassName,
  ariaLabel,
  match,
}: RendererProps) {
  const resolveImage = useEventImageUrl();
  const fav = useFavorites();
  const tasteMatch = useTasteMatch();
  const resolveMatch = match ?? tasteMatch.match;
  const colClass = cardColClassName ?? DEFAULT_CARD_COL;
  return (
    <ul className="list-unstyled row g-3" aria-label={ariaLabel ?? 'Event results'}>
      {items.map((e) => {
        const imageSrc = resolveImage(e.imageUrl);
        const tile = dateTileParts(e);
        const savedClass = fav.isSaved(e.rowKey) ? ' mh-event-card--saved' : '';
        return (
          <li key={e.rowKey} className={colClass}>
            <article
              className={`card h-100 mh-event-card${savedClass}`}
            >
              <button
                type="button"
                className="mh-event-image-btn"
                onClick={() => onSelect(e)}
                aria-label={`View details for ${e.title}`}
              >
                {imageSrc ? (
                  <img src={imageSrc} alt="" loading="lazy" className="card-img-top mh-event-image" />
                ) : (
                  <div className="mh-event-date-tile" aria-hidden="true">
                    {tile.weekday ? (
                      <span className="mh-event-date-tile__weekday">
                        {tile.weekday}
                      </span>
                    ) : null}
                    <span className={`mh-event-date-tile__day${tile.weekday ? '' : ' mh-event-date-tile__day--month'}`}>
                      {tile.day}
                    </span>
                    <span className="mh-event-date-tile__month">
                      {tile.month}
                    </span>
                  </div>
                )}
              </button>
              <div className="card-body d-flex flex-column">
                <h2 className="h6 mb-1">
                  <button
                    type="button"
                    className="btn btn-link p-0 text-start mh-event-title"
                    onClick={() => onSelect(e)}
                  >
                    {e.title}
                  </button>
                  {e.dateFlaggedWrong && (
                    <span
                      className="badge mh-wrong-date-chip ms-2 align-baseline"
                      title="An admin flagged this date/time as wrong"
                    >
                      <i className="bi bi-calendar-x" aria-hidden="true" /> Wrong date
                    </span>
                  )}
                  {isAdmin && onEdit && (
                    <button
                      type="button"
                      className="btn btn-link p-0 ms-2 text-muted small align-baseline"
                      onClick={(ev) => {
                        ev.stopPropagation();
                        onEdit(e);
                      }}
                      aria-label={`Edit ${e.title}`}
                      title="Edit event"
                    >
                      <i className="bi bi-pencil-square" aria-hidden="true" />
                    </button>
                  )}
                  {isAdmin && onHide && (
                    <button
                      type="button"
                      className="btn btn-link p-0 ms-1 text-muted small align-baseline"
                      onClick={(ev) => {
                        ev.stopPropagation();
                        onHide(e);
                      }}
                      aria-label={`Hide ${e.title}`}
                      title="Hide event"
                    >
                      <i className="bi bi-eye-slash" aria-hidden="true" />
                    </button>
                  )}
                  {isAdmin && onRecalculateDate && onMarkWrongDate && (
                    <EventDateMenu
                      event={e}
                      variant="card"
                      recalcBusy={recalculatingIds?.has(e.rowKey) ?? false}
                      markBusy={markingWrongIds?.has(e.rowKey) ?? false}
                      onRecalculate={onRecalculateDate}
                      onMarkWrong={onMarkWrongDate}
                    />
                  )}
                </h2>
                <div className="small text-muted mb-2 d-flex flex-column align-items-start gap-1">
                  <time dateTime={e.startUtc}>{formatRange(e, dateProofRangeOptions(e))}</time>
                  <DateProofWarning event={e} />
                </div>
                {!hideVenue && (e.venueName || e.locationText) && (
                  <p className="small mb-2">
                    {e.venueId ? (
                      <Link to={`/venues/${e.venueId}`}>{e.venueName ?? e.locationText}</Link>
                    ) : (
                      <span>{e.venueName ?? e.locationText}</span>
                    )}
                    {e.neighborhood && <span className="text-muted"> · {e.neighborhood}</span>}
                    {e.venueId && (
                      <EntityFollowChip
                        kind="venue"
                        targetId={e.venueId}
                        targetName={e.venueName ?? e.locationText ?? 'this venue'}
                        offLabel
                        className="ms-2"
                      />
                    )}
                  </p>
                )}
                {!hideSeries && e.seriesId && e.seriesName && (
                  <p className="small mb-2">
                    <Link
                      to={`/series/${e.seriesId}`}
                      className="badge rounded-pill text-bg-info text-dark text-decoration-none"
                    >
                      <i className="bi bi-collection me-1" aria-hidden="true" />
                      {e.seriesName}
                    </Link>
                    <EntityFollowChip
                      kind="series"
                      targetId={e.seriesId}
                      targetName={e.seriesName}
                      offLabel
                      className="ms-2"
                    />
                  </p>
                )}
                <div className="mb-2 d-flex flex-wrap gap-1">
                  <MatchBadge match={resolveMatch(e)} />
                  <EventReviewWarningChips warnings={nonTemporalWarnings(e.reviewWarnings)} />
                  {e.eventType && (
                    <span className="badge text-bg-primary small">{formatTag(e.eventType)}</span>
                  )}
                  {e.priceInfo && <span className="badge text-bg-success small">{e.priceInfo}</span>}
                  {e.ageRestriction && (
                    <span className="badge text-bg-warning text-dark small">
                      {formatTag(e.ageRestriction)}
                    </span>
                  )}
                  {e.recurringPattern && e.recurringPattern !== 'one-time' && (
                    <span
                      className="badge text-bg-info text-dark small"
                      title={
                        e.recurringDaysOfWeek?.length
                          ? `Recurs ${formatTag(e.recurringPattern)} on ${e.recurringDaysOfWeek.join(', ')}`
                          : `Recurs ${formatTag(e.recurringPattern)}`
                      }
                    >
                      🔁 {formatTag(e.recurringPattern)}
                    </span>
                  )}
                  <StaleRecurringChip event={e} size=" small" />
                  {visibleChipTags(e.tags, e).length > 0 && (
                    <EventTagChips
                      event={e}
                      max={3}
                      chipClassName="badge rounded-pill text-bg-light border small"
                      className="d-flex flex-wrap gap-1 align-items-center position-relative"
                      onChange={onEventUpdated}
                    />
                  )}
                  {visibleChipTags(e.tags, e).length === 0 && onEventUpdated && (
                    <EventTagChips
                      event={e}
                      chipClassName="badge rounded-pill text-bg-light border small"
                      className="d-flex flex-wrap gap-1 align-items-center position-relative"
                      onChange={onEventUpdated}
                    />
                  )}
                </div>
                {e.description && <p className="small mb-2 mh-event-desc">{e.description}</p>}
                <div className="mt-auto">
                  <div className="mh-event-card__actions">
                    <EventReactionButtons event={e} />
                  </div>
                  {(e.ticketUrl || e.rawUrl) && (
                    <div className="small d-flex flex-wrap gap-2 mt-2">
                      {e.ticketUrl && (
                        <a href={e.ticketUrl} target="_blank" rel="noreferrer noopener">
                          Tickets / details
                        </a>
                      )}
                      {e.rawUrl && (
                        <a href={e.rawUrl} target="_blank" rel="noreferrer noopener">
                          View original post
                        </a>
                      )}
                    </div>
                  )}
                </div>
              </div>
            </article>
          </li>
        );
      })}
    </ul>
  );
}
